import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Login.css';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');

    if (newPassword !== confirmPassword) {
      setIsError(true);
      setMessage('Passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setIsError(true);
      setMessage('Password must be at least 8 characters.');
      return;
    }

    setIsLoading(true);
    try {
      const { data } = await api.post<{ message: string }>('/auth/reset-password', {
        token,
        newPassword,
      });
      setIsError(false);
      setMessage(data?.message || 'Password reset successfully!');
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err: any) {
      setIsError(true);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to reset password. The link may have expired.';
      setMessage(Array.isArray(msg) ? msg[0] : msg);
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="login-page">
        <div className="login-shell">
          <div className="login-card">
            <div className="login-hero">
              <img src="/toyota-lanka-logo.png" alt="Toyota Lanka" className="logo" />
              <h1>Toyota Lanka</h1>
            </div>
            <div className="login-header">
              <div className="pill">Reset password</div>
              <h2>Invalid link</h2>
              <p className="form-description">
                This reset link is missing or invalid. Please request a new one.
              </p>
            </div>
            <div className="login-form-container">
              <button className="btn-primary" onClick={() => navigate('/login')}>
                Back to sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-shell">
        <div className="login-card">
          <div className="login-hero">
            <img src="/toyota-lanka-logo.png" alt="Toyota Lanka" className="logo" />
            <h1>Toyota Lanka</h1>
          </div>

          <div className="login-header">
            <div className="pill">Reset password</div>
            <h2>Create new password</h2>
            <p className="form-description">Enter a new password for your account.</p>
          </div>

          <div className="login-form-container">
            {message && (
              <div className={`message-box ${isError ? 'error' : 'success'}`}>{message}</div>
            )}

            {!done && (
              <form onSubmit={handleSubmit} className="form-stack">
                <div className="form-group password-group">
                  <label htmlFor="newPassword">New password</label>
                  <div className="password-field">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="newPassword"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      required
                      minLength={8}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="toggle-visibility"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm password</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter new password"
                    required
                    autoComplete="new-password"
                  />
                </div>

                <button type="submit" className="btn-primary" disabled={isLoading}>
                  {isLoading ? 'Resetting…' : 'Reset password'}
                </button>

                <button
                  type="button"
                  className="btn-link"
                  onClick={() => navigate('/login')}
                >
                  Back to sign in
                </button>
              </form>
            )}

            {done && (
              <button className="btn-primary" onClick={() => navigate('/login')}>
                Go to sign in
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
